import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { IEmployeeProfile, IEmployeeSimple, IReviewCycleEmployeeResponse } from '../../models';
import { EmployeeService, PeersService } from '../../services';
import { Subject, takeUntil } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as _ from 'lodash-es';

@Component({
  selector: 'app-add-people-dialog',
  templateUrl: './add-people-dialog.component.html',
  styleUrls: ['./add-people-dialog.component.scss'],
  standalone: false,
})
export class AddPeopleDialogComponent implements OnInit, OnDestroy {
  data = inject<{
    employees: IEmployeeSimple[];
    hideSelectAll?: boolean;
    excludedIds: number[];
    reviewCycleId?: number;
    isAdminContext?: boolean;
  }>(MAT_DIALOG_DATA);
  dialogRef = inject<MatDialogRef<AddPeopleDialogComponent>>(MatDialogRef);
  private employeeService = inject(EmployeeService);
  private peersService = inject(PeersService);

  selectedEmployees: IEmployeeSimple[] = [];
  employeeNames: string[] = [];
  listOfAllEmployees: IEmployeeSimple[] = [];
  filter: string = '';

  private destroy$ = new Subject<void>();

  mapSelectedEmployeeNames(): void {
    this.employeeNames = this.selectedEmployees.map((employee) => employee.fullName);
  }

  ngOnInit(): void {
    this.selectedEmployees = [...this.data.employees];

    if (this.data.reviewCycleId) {
      const participants$ = this.data.isAdminContext
        ? this.peersService.getAssignedParticipantsFull(this.data.reviewCycleId)
        : this.peersService.getAssignedParticipantsFullForUser(this.data.reviewCycleId);
      participants$.pipe(takeUntil(this.destroy$)).subscribe({
        next: (participantsFull: IReviewCycleEmployeeResponse[]) => {
          const employees = participantsFull.map((participant) =>
            this.mapResponseToProfile(participant),
          );
          this.processEmployeeData(employees);
        },
      });
    } else {
      // Get all employees as for participants selection while creating review cycle
      this.employeeService
        .getAllEmployeesProfiles()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data: IEmployeeProfile[]) => {
            this.processEmployeeData(data);
          },
        });
    }
  }

  private processEmployeeData(data: IEmployeeProfile[]) {
    let employees = data.map((el: IEmployeeProfile) => {
      return {
        ...el,
        photo: el.photo ? `data:image/png;base64,${el.photo}` : null,
        name: el.fullName,
      };
    });
    if (this.data.excludedIds?.length) {
      const excludedIdsSet = new Set(this.data.excludedIds);
      employees = employees.filter((e) => !excludedIdsSet.has(e.id));
    }
    // Filter out initially selected employees
    const selectedIdsSet = new Set(this.selectedEmployees.map((e) => e.id));
    employees = employees.filter((e) => !selectedIdsSet.has(e.id));
    employees = _.sortBy(employees, 'fullName');
    this.listOfAllEmployees = employees;
  }

  removeOrAddEmployee(employee: IEmployeeSimple) {
    const index = this.selectedEmployees.findIndex((item) => item.id === employee.id);
    if (index >= 0) {
      this.selectedEmployees.splice(index, 1);
    } else {
      this.selectedEmployees.push(employee);
    }
    this.mapSelectedEmployeeNames();
  }

  closeDialog() {
    this.dialogRef.close({
      employees: this.selectedEmployees.sort((a, b) =>
        a.fullName.trim().localeCompare(b.fullName.trim()),
      ),
    });
  }

  handleSelectAllClick(): void {
    const listOfAllEmployeesIds = new Set(this.listOfAllEmployees.map((e) => e.id));
    const allListEmployeesSelected = this.listOfAllEmployees.every((employee) =>
      this.selectedEmployees.some((selected) => selected.id === employee.id),
    );

    if (allListEmployeesSelected) {
      // Remove all listOfAllEmployees from selectedEmployees
      this.selectedEmployees = this.selectedEmployees.filter(
        (e) => !listOfAllEmployeesIds.has(e.id),
      );
    } else {
      // Add all listOfAllEmployees to selectedEmployees
      const currentSelectedIds = new Set(this.selectedEmployees.map((e) => e.id));
      const newEmployees = this.listOfAllEmployees.filter((e) => !currentSelectedIds.has(e.id));
      this.selectedEmployees = [...this.selectedEmployees, ...newEmployees];
    }
    this.mapSelectedEmployeeNames();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private mapResponseToProfile(response: IReviewCycleEmployeeResponse): IEmployeeProfile {
    return {
      ...response,
      id: response.employeeId,
      fullName: response.name,
      email: null,
    } as IEmployeeProfile;
  }
}
